import { type ReactNode, useMemo, useState } from "react";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { cx } from "../cx";
import { Icon } from "../Icon";
import { Popover, useDisclosure } from "../Popover";
import styles from "./MultiSelectFilter.module.css";

export interface FilterOption {
  value: string;
  label: string;
  /** Group header the row sits under (`GROUPS`, `USERS`, client names...). */
  group?: string;
}

export type FilterStatus = "ACTIVE" | "ARCHIVED" | "ALL";

const STATUS_LABELS: Record<FilterStatus, string> = { ACTIVE: "Active", ARCHIVED: "Archived", ALL: "All" };

export interface MultiSelectFilterProps {
  /** Trigger label, e.g. `Team`, `Project`. */
  label: string;
  options: FilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
  searchPlaceholder?: string;
  /** Shows the `SHOW` status row; the parent refetches options when it changes. */
  status?: FilterStatus;
  onStatusChange?: (status: FilterStatus) => void;
  statusOptions?: FilterStatus[];
  /** Adds an APPLY button in a footer; the live Team filter applies from the page's APPLY FILTER instead. */
  onApply?: () => void;
  applyLabel?: string;
  selectAllLabel?: string;
  emptyText?: string;
  footer?: ReactNode;
  width?: number;
  className?: string;
}

export function MultiSelectFilter({
  label,
  options,
  value,
  onChange,
  searchPlaceholder = "Search",
  status,
  onStatusChange,
  statusOptions = ["ACTIVE", "ARCHIVED", "ALL"],
  onApply,
  applyLabel = "Apply",
  selectAllLabel = "Select all",
  emptyText = "No matching items",
  footer,
  width = 363,
  className,
}: MultiSelectFilterProps) {
  const { open, toggle, close, anchorRef } = useDisclosure<HTMLButtonElement>();
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);
  const groups = useMemo(() => {
    const out: Array<{ group: string | undefined; items: FilterOption[] }> = [];
    for (const o of visible) {
      const g = out.find((x) => x.group === o.group);
      if (g) g.items.push(o);
      else out.push({ group: o.group, items: [o] });
    }
    return out;
  }, [visible]);

  const visibleValues = visible.map((o) => o.value);
  const selectedVisible = visibleValues.filter((v) => value.includes(v));
  const all = visibleValues.length > 0 && selectedVisible.length === visibleValues.length;
  const some = selectedVisible.length > 0 && !all;

  const toggleOne = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const toggleAll = () =>
    onChange(all ? value.filter((v) => !visibleValues.includes(v)) : Array.from(new Set([...value, ...visibleValues])));

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={cx(styles.trigger, value.length > 0 && styles.triggerActive, className)}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        {label}
        {value.length > 0 && <span className={styles.count}>{value.length}</span>}
        <Icon name="chevron-down" size={14} />
      </button>
      <Popover
        open={open}
        onClose={() => {
          close();
          setQuery("");
        }}
        anchorRef={anchorRef}
        placement="bottom-start"
        className={styles.panel}
        style={{ width }}
        role="dialog"
        aria-label={label}
      >
        <div className={styles.search}>
          <Icon name="search" size={16} className={styles.searchIcon} />
          <input
            data-autofocus
            className={styles.searchInput}
            value={query}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {status && onStatusChange && (
          <div className={styles.statusRow}>
            <span className={styles.header}>Show</span>
            {/* Native select: a nested Popover would count as an outside click and close this panel. */}
            <select
              className={styles.statusSelect}
              value={status}
              aria-label="Show"
              onChange={(e) => onStatusChange(e.target.value as FilterStatus)}
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className={styles.list}>
          {visible.length === 0 ? (
            <div className={styles.empty}>{emptyText}</div>
          ) : (
            <>
              <Checkbox
                className={styles.row}
                label={selectAllLabel}
                checked={all}
                indeterminate={some}
                onChange={toggleAll}
              />
              {groups.map((g) => (
                <div key={g.group ?? "__none__"}>
                  {g.group && <div className={cx(styles.header, styles.groupHeader)}>{g.group}</div>}
                  {g.items.map((o) => (
                    <Checkbox
                      key={o.value}
                      className={styles.row}
                      label={o.label}
                      checked={value.includes(o.value)}
                      onChange={() => toggleOne(o.value)}
                    />
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
        {(onApply || footer) && (
          <div className={styles.footer}>
            {footer}
            {onApply && (
              <Button
                size="sm"
                onClick={() => {
                  onApply();
                  close();
                }}
              >
                {applyLabel}
              </Button>
            )}
          </div>
        )}
      </Popover>
    </>
  );
}
